import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import AppLayout from '../components/AppLayout';

function EventFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const cellId = searchParams.get('cell_id');
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: cellId ? 'celula' : 'reuniao_lideres',
    event_date: new Date().toISOString().split('T')[0],
    start_time: '',
  });

  const [errors, setErrors] = useState({});

  // Carrega evento se estiver editando
  const { data: eventData, isLoading: loadingEvent } = useQuery({
    queryKey: ['event', id],
    queryFn: async () => {
      const response = await api.get(`/events/${id}`);
      return response.data.data;
    },
    enabled: isEdit,
  });

  // Preenche formulário quando evento é carregado
  useEffect(() => {
    if (eventData && isEdit) {
      setFormData({
        title: eventData.title || '',
        description: eventData.description || '',
        event_type: eventData.event_type || 'celula',
        event_date: eventData.event_date ? eventData.event_date.split('T')[0] : '',
        start_time: eventData.start_time || '',
      });
    }
  }, [eventData, isEdit]);

  // Mutation para criar/atualizar evento
  const eventMutation = useMutation({
    mutationFn: async (data) => {
      if (isEdit) {
        return api.put(`/events/${id}`, data);
      } else {
        return api.post('/events', data);
      }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      
      const eventId = response.data.data.id || id;
      
      // Se veio do fluxo de Checkpoint (tem cell_id), volta para o checkpoint
      if (cellId && !isEdit) {
        navigate(`/checkpoint?cell_id=${cellId}&event_id=${eventId}`);
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error) => {
      const errorData = error.response?.data;
      if (errorData?.errors) {
        setErrors(errorData.errors);
      } else {
        setErrors({ general: errorData?.message || error.message || 'Erro ao salvar evento' });
      }
    },
  });

  // Handler de mudança de campo
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler de submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validação básica
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Título é obrigatório';
    }
    if (!formData.event_date) {
      newErrors.event_date = 'Data do evento é obrigatória';
    }
    if (!formData.event_type) {
      newErrors.event_type = 'Tipo de evento é obrigatório';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Prepara dados para envio
    const submitData = {
      title: formData.title.trim(),
      description: formData.description.trim() || null,
      event_type: formData.event_type,
      event_date: formData.event_date,
      start_time: formData.start_time || null,
    };

    eventMutation.mutate(submitData);
  };

  const eventTypes = [
    { value: 'celula', label: 'Reunião de Célula' },
    { value: 'culto_domingo', label: 'Culto de Domingo' },
    { value: 'reuniao_lideres', label: 'Reunião de Líderes' },
    { value: 'conferencia', label: 'Conferência' },
    { value: 'encontro_deus', label: 'Encontro com Deus' },
    { value: 'batismo_aguas', label: 'Batismo nas Águas' },
    { value: 'outro', label: 'Outro' },
  ];

  if (isEdit && loadingEvent) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="event-form-page" style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <button
            className="btn btn-text"
            onClick={() => navigate(cellId ? '/checkpoint/select' : '/dashboard')}
            style={{ marginBottom: '1rem' }}
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Voltar
          </button>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold' }}>
            {isEdit ? 'Editar Evento' : 'Criar Novo Evento'}
          </h1>
          {cellId && (
            <p style={{ color: 'var(--text-muted)', marginTop: '0.5rem' }}>
              Crie este evento para iniciar o <strong>Checkpoint</strong> da sua célula.
            </p>
          )}
        </div>

        {errors.general && (
          <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem' }}>
              Informações Básicas
            </h2>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">
                Título do Evento <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <input
                type="text"
                name="title"
                className={`form-input ${errors.title ? 'error' : ''}`}
                value={formData.title}
                onChange={handleChange}
                required
                placeholder="Ex: Reunião Célula Esperança"
              />
              {errors.title && (
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                  {errors.title}
                </span>
              )}
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label className="form-label">
                Tipo de Evento <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <select
                name="event_type"
                className="form-input"
                value={formData.event_type}
                onChange={handleChange}
                required
              >
                {eventTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div className="form-group">
                <label className="form-label">
                  Data <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <input
                  type="date"
                  name="event_date"
                  className={`form-input ${errors.event_date ? 'error' : ''}`}
                  value={formData.event_date}
                  onChange={handleChange}
                  required
                />
                {errors.event_date && (
                  <span style={{ color: 'var(--danger)', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                    {errors.event_date}
                  </span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Horário de Início</label>
                <input
                  type="time"
                  name="start_time"
                  className="form-input"
                  value={formData.start_time}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Descrição Opcional</label>
              <textarea
                name="description"
                className="form-input"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Observações relativas ao evento..."
              />
            </div>
          </div>

          {/* Botões de Ação */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(cellId ? '/checkpoint/select' : '/dashboard')}
              disabled={eventMutation.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={eventMutation.isPending}
            >
              {eventMutation.isPending ? (
                <LoadingSpinner size="sm" />
              ) : (
                <span className="material-symbols-outlined">save</span>
              )}
              &nbsp; {isEdit ? 'Guardar Alteraçōes' : 'Criar Evento e Avançar'}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}

export default EventFormPage;
